import type { ComponentType } from 'react';

import { cn } from '@/lib/utils';

export interface ViewModeOption<T extends string> {
  value: T;
  label: string;
  /** Any lucide-react icon component. */
  icon: ComponentType<{ className?: string }>;
}

interface ViewModeToggleProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  modes: readonly ViewModeOption<T>[];
  /** Accessible label for the group. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Segmented control used by list pages to switch representations
 * (Table / Cards / Grouped / List / …). Typed on the page-specific mode union
 * so each page only allows its own valid values.
 *
 * Pattern lifted out of Notes — applied across CRM/HRM/Finance list pages.
 */
export function ViewModeToggle<T extends string>({
  value,
  onChange,
  modes,
  ariaLabel = 'View mode',
  className,
}: ViewModeToggleProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'border-input bg-background inline-flex overflow-hidden rounded-md border',
        className,
      )}
    >
      {modes.map(({ value: v, label, icon: Icon }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-pressed={value === v}
          aria-label={label}
          title={label}
          className={cn(
            'inline-flex h-8 items-center gap-1 px-2 text-xs transition-colors',
            value === v
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          <Icon className="size-4" />
        </button>
      ))}
    </div>
  );
}
